import React, { useEffect, useState } from "react";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { useHistory, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { sliceKey, reducer, actions } from "./slice";
import { packageAddSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import AddPackageForm from "../../../../forms/AddPackageForm";
import schema from "../../../../schemas/addPackageSchema";
import { reverse } from "named-urls";
import { useAuth } from "../../../../contexts/auth";

const PackageAdd = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: packageAddSaga });
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const isCompanyAdmin = auth.isCompanyAdmin;

  useEffect(() => {
    if (!isCompanyAdmin) {
      setLoadingStates((prev) => ({ ...prev, companies: true }));
      dispatch(
        actions.fetchCompanies({
          onSuccess: () =>
            setLoadingStates((prev) => ({ ...prev, companies: false })),
          onFailure: () => {
            setLoadingStates((prev) => ({ ...prev, companies: false }));
            notification.error({
              message: "Failed to fetch companies",
              description: "Unable to load companies. Please try again.",
            });
          },
        })
      );
    }
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, isCompanyAdmin]);

  const companies = useSelector(selectors.selectCompanies) || [];
  const commodities = useSelector(selectors.selectCommodities);
  const tests = useSelector(selectors.selectTests);
  const [loadingStates, setLoadingStates] = useState({
    packageCreating: false,
    companies: false,
    commodities: false,
    tests: false,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      company: isCompanyAdmin ? user.company._id : "",
      tests: [],
      commodity: "",
      sampleVariant: "",
      isDefaultPackage: false,
      image: [],
    },
  });

  const selectedCompany = watch("company");
  const selectedCommodity = watch("commodity");

  const getTestCategories = () => {
    const testCategories = [];

    tests.forEach((test) => {
      const children = test.options.map((child) => {
        let obj = {
          title: (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <p>{child.label}</p>
              <p>₹{child.price}</p>
            </div>
          ),
          key: child.value,
          value: child.value,
        };

        return obj;
      });
      testCategories.push({
        title: test.label,
        key: test.value,
        value: test.value,
        children: children,
      });
    });

    return testCategories;
  };

  useEffect(() => {
    if (selectedCompany) {
      setLoadingStates((prev) => ({ ...prev, commodities: true }));
      dispatch(
        actions.fetchCommodities({
          companyId: selectedCompany,
          onSuccess: () =>
            setLoadingStates((prev) => ({ ...prev, commodities: false })),
          onFailure: () => {
            setLoadingStates((prev) => ({ ...prev, commodities: false }));
            notification.error({
              message: "Failed to fetch commodities",
              description: "Unable to load commodities. Please try again.",
            });
          },
        })
      );
      setValue("commodity", "");
    }
  }, [dispatch, selectedCompany, setValue]);

  useEffect(() => {
    if (selectedCommodity.length > 0) {
      setLoadingStates((prev) => ({ ...prev, tests: true }));
      dispatch(
        actions.fetchTests({
          companyId: selectedCompany,
          commodityId: selectedCommodity,
          onSuccess: () =>
            setLoadingStates((prev) => ({ ...prev, tests: false })),
          onFailure: () => {
            setLoadingStates((prev) => ({ ...prev, tests: false }));
            notification.error({
              message: "Failed to fetch tests",
              description: "Unable to load tests. Please try again.",
            });
          },
        })
      );
    }
    setValue("tests", []);
  }, [dispatch, selectedCommodity, selectedCompany, setValue]);

  const handleSuccess = () => {
    setLoadingStates((prev) => ({ ...prev, packageCreating: false }));
    notification.success({
      message: "Package got added successfully",
    });
    history.push(routes.backOffice.packages.list);
  };

  const handleFailure = () => {
    setLoadingStates((prev) => ({ ...prev, packageCreating: false }));
    notification.error({
      message: "Failed to create package",
      description:
        "An error occured while creating the package. Please try again after some time.",
    });
  };

  const onSubmit = (data) => {
    setLoadingStates((prev) => ({ ...prev, packageCreating: true }));
    const payload = {
      onSuccess: handleSuccess,
      data: { ...data, image: data.image?.[0]?.url },
      onFailure: handleFailure,
    };

    dispatch(actions.createPackage(payload));
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Create Package"
        extra={[
          <Button key="cancel">
            <Link to={reverse(routes.backOffice.packages.list)}>Cancel</Link>
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={loadingStates.packageCreating}
            onClick={handleSubmit(onSubmit)}
          >
            Create
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <AddPackageForm
          control={control}
          watch={watch}
          errors={errors}
          isCompanyAdmin={isCompanyAdmin}
          loading={{
            companies: loadingStates.companies,
            commodities: loadingStates.commodities,
            tests: loadingStates.tests,
          }}
          options={{
            companies: companies,
            catalog: getTestCategories(),
            commodities,
          }}
        />
      </div>
    </div>
  );
};

export default PackageAdd;
